import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Tag,
} from 'antd';
import { Link } from 'react-router-dom';
import { AIProvider, listProviders } from '../../AIProviders/api';
import { errorMessage } from '../../MarketExplorer/api';
import { cancelJob } from '../api';
import { fetchCorpusJob, fetchCorpusJobs, startBatch } from './api';
import { BatchRequest, CorpusJob, CorpusState, Operation } from './types';
import ProposalReview from './ProposalReview';

const live = (job: CorpusJob | null) =>
  Boolean(job && ['PENDING', 'BLOCKED', 'RUNNING'].includes(job.status));
const operationLabels: Record<Operation, string> = {
  ingest: 'Import PM news into corpus',
  prepare: 'Prepare headline and tag suggestions',
  review: 'Read prepared suggestions',
};
const Processing: React.FC<{
  snapshot: CorpusState | null;
  onOpenJob: (id: string) => void;
  onWorkChanged: () => void;
}> = ({ snapshot, onOpenJob, onWorkChanged }) => {
  const [operation, setOperation] = useState<Operation>('ingest');
  const [limit, setLimit] = useState(100);
  const [afterId, setAfterId] = useState(0);
  const [concurrency, setConcurrency] = useState(16);
  const maxConcurrency = Math.max(
    1,
    Math.min(192, snapshot?.pipeline_max_concurrency || 64),
  );
  const [provider, setProvider] = useState<string>();
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [providerError, setProviderError] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [jobs, setJobs] = useState<CorpusJob[]>([]);
  const [selected, setSelected] = useState<CorpusJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true);
  const openRequest = useRef<AbortController | null>(null);
  const selectedRef = useRef<string | null>(null);
  const refreshJobs = async () => {
    try {
      const page = await fetchCorpusJobs();
      if (active.current)
        setJobs(
          page.results.filter(job =>
            ['ingest', 'prepare', 'review'].includes(job.request?.operation),
          ),
        );
    } catch (reason) {
      if (active.current) setError(errorMessage(reason));
    }
  };
  useEffect(() => {
    active.current = true;
    void refreshJobs();
    void listProviders()
      .then(rows => {
        if (active.current)
          setProviders(
            rows.filter(
              row => row.enabled && row.bulk_only && row.kind === 'vllm',
            ),
          );
      })
      .catch(reason => {
        if (active.current) setProviderError(errorMessage(reason));
      });
    return () => {
      active.current = false;
      openRequest.current?.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selected || !live(selected)) return;
    const controller = new AbortController();
    let timer: number;
    const poll = async () => {
      try {
        const fresh = await fetchCorpusJob(selected.id, controller.signal);
        if (controller.signal.aborted) return;
        setSelected(fresh);
        setError('');
        if (!live(fresh)) {
          void refreshJobs();
          onWorkChanged();
        }
      } catch (reason) {
        if (!controller.signal.aborted) setError(errorMessage(reason));
      }
      if (!controller.signal.aborted) timer = window.setTimeout(poll, 3000);
    };
    timer = window.setTimeout(poll, 2000);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [selected?.id, selected?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = async (id: string) => {
    openRequest.current?.abort();
    const controller = new AbortController();
    openRequest.current = controller;
    selectedRef.current = id;
    setOpening(true);
    setSelected(null);
    setError('');
    try {
      const job = await fetchCorpusJob(id, controller.signal);
      if (!controller.signal.aborted) setSelected(job);
    } catch (reason) {
      if (!controller.signal.aborted) setError(errorMessage(reason));
    } finally {
      if (!controller.signal.aborted) setOpening(false);
    }
  };
  const ready = snapshot?.enabled && snapshot.state === 'ready';
  const submit = async (request: BatchRequest) => {
    if (busy || !ready) return;
    setBusy(true);
    setError('');
    openRequest.current?.abort();
    try {
      const job = await startBatch(request);
      if (!active.current) return;
      setSelected(job);
      selectedRef.current = job.id;
      setOpening(false);
      void refreshJobs();
      onWorkChanged();
    } catch (reason) {
      if (active.current) setError(errorMessage(reason));
    } finally {
      if (active.current) setBusy(false);
    }
  };
  const start = () =>
    submit({
      operation,
      limit: operation === 'review' ? Math.min(limit, 100) : limit,
      after_id: afterId,
      ...(operation === 'prepare'
        ? {
            provider_name: provider,
            concurrency: Math.min(concurrency, maxConcurrency),
          }
        : {}),
      ...(operation === 'review' ? { query: query.trim(), status } : {}),
    });
  const cancel = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError('');
    try {
      await cancelJob(selected.id);
      await open(selected.id);
      await refreshJobs();
      onWorkChanged();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };
  const result = selected?.result;
  const remainingNewsIds = selected?.request.news_ids?.filter(
    id => result?.next_after_id != null && id > result.next_after_id,
  );
  const failedNewsIds = Array.from(
    new Set(result?.failed_news_ids || []),
  ).slice(0, 1000);
  const canContinue =
    result?.has_more &&
    result.next_after_id != null &&
    (!remainingNewsIds || remainingNewsIds.length > 0);
  return (
    <section aria-label="Bounded corpus processing">
      <div className="corpus-news__batch-form">
        <label className="corpus-news__operation">
          Operation
          <Select
            value={operation}
            onChange={setOperation}
            options={Object.entries(operationLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </label>
        <label>
          Batch size
          <InputNumber
            aria-label="Batch size"
            value={limit}
            min={1}
            max={operation === 'review' ? 100 : 1000}
            precision={0}
            onChange={value => setLimit(value || 1)}
          />
        </label>
        {operation === 'prepare' && (
          <label className="corpus-news__provider">
            Pinned provider
            <Select
              aria-label="Pinned provider"
              value={provider}
              onChange={setProvider}
              placeholder="Choose a dedicated bulk vLLM provider"
              showSearch
              optionFilterProp="label"
              options={providers.map(value => ({
                value: value.name,
                label: `${value.name} · ${value.model}`,
              }))}
            />
          </label>
        )}
        <Button
          type="primary"
          loading={busy}
          disabled={
            !ready || live(selected) || (operation === 'prepare' && !provider)
          }
          onClick={start}
        >
          {operation === 'review' ? 'Load proposal batch' : 'Start batch'}
        </Button>
      </div>
      <p className="corpus-news__muted">
        {operation === 'ingest'
          ? 'Copy a bounded page of current PM articles into the corpus. Original PM articles are not changed.'
          : operation === 'prepare'
            ? 'Generate suggestions with the provider selected here. Nothing is applied to PM until you review and save it.'
            : 'Read saved proposals from the corpus. This step makes no model calls.'}
      </p>
      {!ready && (
        <Alert
          type="info"
          showIcon
          title={
            snapshot?.state === 'waking'
              ? 'Waiting for the corpus worker to finish waking.'
              : snapshot?.state === 'draining'
                ? 'Corpus work is draining. Wait for it to stop before turning it on again.'
                : 'Turn on the corpus and wait for ready before starting a batch.'
          }
        />
      )}
      {operation === 'prepare' && !providers.length && (
        <Alert
          type="warning"
          showIcon
          title="An enabled, dedicated bulk vLLM provider is required"
          description={
            <>
              <Link to="/dashboard/ai-providers">Manage AI providers</Link>.
              This workspace does not create or start model pods.
            </>
          }
        />
      )}
      {operation === 'prepare' && providerError && (
        <Alert type="error" title={providerError} />
      )}
      <Collapse
        ghost
        items={[
          {
            key: 'options',
            label: 'Batch options',
            children: (
              <div className="corpus-news__batch-form">
                <label>
                  Start after ID
                  <InputNumber
                    aria-label="Start after ID"
                    min={0}
                    precision={0}
                    value={afterId}
                    onChange={value => setAfterId(value || 0)}
                  />
                </label>
                {operation === 'prepare' && (
                  <label>
                    Concurrent requests
                    <InputNumber
                      aria-label="Concurrent requests"
                      min={1}
                      max={maxConcurrency}
                      precision={0}
                      value={Math.min(concurrency, maxConcurrency)}
                      onChange={value => setConcurrency(value || 1)}
                    />
                  </label>
                )}
                {operation === 'review' && (
                  <>
                    <label>
                      Proposal search
                      <Input
                        value={query}
                        maxLength={200}
                        onChange={event => setQuery(event.target.value)}
                      />
                    </label>
                    <label>
                      Model status
                      <Select
                        value={status}
                        onChange={setStatus}
                        options={[
                          'all',
                          'pending',
                          'accepted',
                          'needs_review',
                          'failed',
                        ].map(value => ({
                          value,
                          label: value.replace(/_/g, ' '),
                        }))}
                      />
                    </label>
                  </>
                )}
                <p className="corpus-news__muted">
                  Resume uses the saved batch cursor. All operations use PM news
                  IDs.
                </p>
              </div>
            ),
          },
        ]}
      />
      {error && <Alert type="error" showIcon title={error} />}
      <div className="corpus-news__run-picker">
        <label>
          Recent corpus jobs
          <Select
            aria-label="Recent corpus jobs"
            placeholder="Choose a recorded job"
            value={selected?.id || (opening ? selectedRef.current : undefined)}
            onChange={open}
            options={jobs.map(job => ({
              value: job.id,
              label: `${operationLabels[job.request?.operation] || job.kind} · ${job.status.toLowerCase()} · ${new Date(job.created_at).toLocaleString()}`,
            }))}
          />
        </label>
        <Button onClick={refreshJobs}>Refresh jobs</Button>
      </div>
      {opening && <Spin />}
      {selected && (
        <div className="corpus-news__run-result">
          <Space wrap>
            <Tag
              color={
                selected.status === 'SUCCEEDED'
                  ? 'success'
                  : live(selected)
                    ? 'processing'
                    : 'default'
              }
            >
              {selected.status}
            </Tag>
            <strong>
              {operationLabels[selected.request?.operation] || selected.kind}
            </strong>
            <Button type="link" onClick={() => onOpenJob(selected.id)}>
              Job details
            </Button>
            {['PENDING', 'BLOCKED'].includes(selected.status) && (
              <Button onClick={cancel} loading={busy}>
                Cancel queued job
              </Button>
            )}
          </Space>
          {live(selected) && (
            <p>
              <Spin size="small" />{' '}
              {selected.status === 'RUNNING'
                ? 'Processing this bounded batch. Progress is saved as articles finish; you can leave this page and reopen the job.'
                : 'Waiting for a corpus worker.'}
            </p>
          )}
          {(selected.error || selected.refusal) && (
            <Alert
              type="error"
              showIcon
              title={errorMessage(selected.error || selected.refusal)}
            />
          )}
          {result && (
            <>
              <p>
                {result.processed} processed
                {Object.entries(result.counts || {})
                  .map(
                    ([key, count]) => ` · ${count} ${key.replace(/_/g, ' ')}`,
                  )
                  .join('')}
              </p>
              {result.limitations?.map(note => (
                <p key={note} className="corpus-news__muted">
                  {note}
                </p>
              ))}
              {!!result.items?.length && (
                <ProposalReview items={result.items} jobId={selected.id} />
              )}
              {result.operation === 'prepare' &&
                result.processed > 0 &&
                !live(selected) && (
                  <Button
                    disabled={!ready || busy}
                    onClick={() =>
                      submit({
                        operation: 'review',
                        after_id: selected.request.after_id,
                        limit: Math.min(selected.request.limit, 100),
                        status: 'all',
                        ...(selected.request.news_ids
                          ? {
                              news_ids: selected.request.news_ids.slice(0, 100),
                            }
                          : {}),
                      })
                    }
                  >
                    Review prepared news
                  </Button>
                )}
              {result.operation === 'prepare' &&
                result.processed > 0 &&
                !live(selected) &&
                (selected.request.news_ids?.length || 0) > 100 && (
                  <p className="corpus-news__muted">
                    Review opens the first 100 selected news IDs.
                  </p>
                )}
              {result.operation === 'prepare' &&
                !live(selected) &&
                failedNewsIds.length > 0 && (
                  <Button
                    disabled={!ready || busy}
                    onClick={() =>
                      submit({
                        ...selected.request,
                        news_ids: failedNewsIds,
                        after_id: 0,
                        limit: Math.min(
                          1000,
                          Math.max(
                            failedNewsIds.length,
                            selected.request.limit,
                          ),
                        ),
                      })
                    }
                  >
                    Retry failed news
                  </Button>
                )}
              {result.operation === 'review' && !result.items?.length && (
                <Empty description="No prepared suggestions match this batch." />
              )}
              {canContinue && (
                <Button
                  disabled={!ready || busy || live(selected)}
                  onClick={() =>
                    submit({
                      ...selected.request,
                      after_id: result.next_after_id!,
                      ...(remainingNewsIds
                        ? { news_ids: remainingNewsIds }
                        : {}),
                    })
                  }
                >
                  Continue next batch
                </Button>
              )}
              {!result.has_more && !live(selected) && (
                <p className="corpus-news__muted">
                  End of this batch selection.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};
export default Processing;
